// M10 Pose Tetris — raise dumbbell-style arms to steer falling pieces.
// Spec: /microgames/10-pose-tetris.md

import type {
  GameContext,
  InputFrame,
  Microgame,
  MicrogameResult,
  Point,
} from '@pose-game/core';

const NAME = '10-pose-tetris';
const DURATION_SEC = 40;
const CALIBRATION_SEC = 1.5;
const COLS = 10;
const ROWS = 20;
const TARGET_LINES = 3;
const MOVE_COOLDOWN_SEC = 0.32;
const ARM_RAISE_TOL_PX = 20;
const SQUAT_TRIGGER = 0.45;
const SQUAT_RELEASE = 0.3;
const FALL_INTERVAL_MAX = 1.4;
const FALL_INTERVAL_MIN = 0.7;
const FALL_RAMP_PER_SCORE = 0.012;
const LINE_FLASH_SEC = 0.18;

type Kind = 'I' | 'O' | 'T' | 'S' | 'Z' | 'J' | 'L';
const KINDS: Kind[] = ['I', 'O', 'T', 'S', 'Z', 'J', 'L'];

const COLOR_BY_KIND: Record<Kind, string> = {
  I: '#00f0f0',
  O: '#f0f000',
  T: '#a000f0',
  S: '#00f000',
  Z: '#f00000',
  J: '#0000f0',
  L: '#f0a000',
};

const SHAPES: Record<Kind, ReadonlyArray<ReadonlyArray<Point>>> = {
  I: [
    [{ x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 3, y: 1 }],
    [{ x: 2, y: 0 }, { x: 2, y: 1 }, { x: 2, y: 2 }, { x: 2, y: 3 }],
    [{ x: 0, y: 2 }, { x: 1, y: 2 }, { x: 2, y: 2 }, { x: 3, y: 2 }],
    [{ x: 1, y: 0 }, { x: 1, y: 1 }, { x: 1, y: 2 }, { x: 1, y: 3 }],
  ],
  O: [
    [{ x: 1, y: 0 }, { x: 2, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 }],
    [{ x: 1, y: 0 }, { x: 2, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 }],
    [{ x: 1, y: 0 }, { x: 2, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 }],
    [{ x: 1, y: 0 }, { x: 2, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 }],
  ],
  T: [
    [{ x: 1, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }],
    [{ x: 1, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 1, y: 2 }],
    [{ x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 1, y: 2 }],
    [{ x: 1, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 1, y: 2 }],
  ],
  S: [
    [{ x: 1, y: 0 }, { x: 2, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }],
    [{ x: 1, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 2, y: 2 }],
    [{ x: 1, y: 1 }, { x: 2, y: 1 }, { x: 0, y: 2 }, { x: 1, y: 2 }],
    [{ x: 0, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 1, y: 2 }],
  ],
  Z: [
    [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 }],
    [{ x: 2, y: 0 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 1, y: 2 }],
    [{ x: 0, y: 1 }, { x: 1, y: 1 }, { x: 1, y: 2 }, { x: 2, y: 2 }],
    [{ x: 1, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 0, y: 2 }],
  ],
  J: [
    [{ x: 0, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }],
    [{ x: 1, y: 0 }, { x: 2, y: 0 }, { x: 1, y: 1 }, { x: 1, y: 2 }],
    [{ x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 2, y: 2 }],
    [{ x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 2 }, { x: 1, y: 2 }],
  ],
  L: [
    [{ x: 2, y: 0 }, { x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }],
    [{ x: 1, y: 0 }, { x: 1, y: 1 }, { x: 1, y: 2 }, { x: 2, y: 2 }],
    [{ x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }, { x: 0, y: 2 }],
    [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 1, y: 2 }],
  ],
};

interface ActivePiece {
  kind: Kind;
  rotation: number;
  x: number;
  y: number;
}

interface State {
  ctx: GameContext;
  board: number[][];
  current: ActivePiece | null;
  bag: Kind[];
  elapsed: number;
  calibration: number;
  linesCleared: number;
  score: number;
  fallTimer: number;
  fallInterval: number;
  lastMoveLeftAt: number;
  lastMoveRightAt: number;
  rotateLatch: boolean;
  dropLatch: boolean;
  gameOver: boolean;
  endedEarly: boolean;
  /** Banner timer for line clear flash; 0 = no flash. */
  flashTimer: number;
  flashRows: number[];
}

let state: State | null = null;

function emptyBoard(): number[][] {
  const b: number[][] = [];
  for (let y = 0; y < ROWS; y++) {
    const row: number[] = [];
    for (let x = 0; x < COLS; x++) row.push(0);
    b.push(row);
  }
  return b;
}

function colorIndexForKind(kind: Kind): number {
  return KINDS.indexOf(kind) + 1;
}

function colorFromIndex(idx: number): string {
  const kind = KINDS[idx - 1];
  if (kind == null) return '#888';
  return COLOR_BY_KIND[kind];
}

function refillBag(s: State): void {
  const bag: Kind[] = [...KINDS];
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(s.ctx.rng() * (i + 1));
    const a = bag[i];
    const b = bag[j];
    if (a == null || b == null) continue;
    bag[i] = b;
    bag[j] = a;
  }
  s.bag.push(...bag);
}

function shapeAt(kind: Kind, rotation: number): ReadonlyArray<Point> {
  const rotations = SHAPES[kind];
  const rot = ((rotation % rotations.length) + rotations.length) % rotations.length;
  const cells = rotations[rot];
  return cells ?? rotations[0] ?? [];
}

function collides(
  s: State,
  kind: Kind,
  rotation: number,
  px: number,
  py: number,
): boolean {
  const cells = shapeAt(kind, rotation);
  for (const c of cells) {
    const x = px + c.x;
    const y = py + c.y;
    if (x < 0 || x >= COLS || y >= ROWS) return true;
    if (y < 0) continue;
    const row = s.board[y];
    if (row == null) return true;
    const cell = row[x];
    if (cell == null) return true;
    if (cell !== 0) return true;
  }
  return false;
}

function spawnPiece(s: State): void {
  if (s.bag.length < 1) refillBag(s);
  const kind = s.bag.shift();
  if (kind == null) {
    s.gameOver = true;
    return;
  }
  const piece: ActivePiece = {
    kind,
    rotation: 0,
    x: Math.floor(COLS / 2) - 2,
    y: 0,
  };
  if (collides(s, piece.kind, piece.rotation, piece.x, piece.y)) {
    s.current = null;
    s.gameOver = true;
    s.ctx.audio.lose();
    return;
  }
  s.current = piece;
}

function clearFullRows(s: State): number {
  const cleared: number[] = [];
  for (let y = 0; y < ROWS; y++) {
    const row = s.board[y];
    if (row == null) continue;
    let full = true;
    for (let x = 0; x < COLS; x++) {
      if (row[x] === 0) { full = false; break; }
    }
    if (full) cleared.push(y);
  }
  if (cleared.length === 0) return 0;
  const newBoard: number[][] = [];
  for (let y = 0; y < ROWS; y++) {
    if (cleared.includes(y)) continue;
    const row = s.board[y];
    if (row == null) continue;
    newBoard.push(row.slice());
  }
  while (newBoard.length < ROWS) {
    const blank: number[] = [];
    for (let x = 0; x < COLS; x++) blank.push(0);
    newBoard.unshift(blank);
  }
  s.board = newBoard;
  s.flashTimer = LINE_FLASH_SEC;
  s.flashRows = cleared.slice();
  return cleared.length;
}

function lockPiece(s: State): void {
  const piece = s.current;
  if (piece == null) return;
  const color = colorIndexForKind(piece.kind);
  const cells = shapeAt(piece.kind, piece.rotation);
  for (const c of cells) {
    const x = piece.x + c.x;
    const y = piece.y + c.y;
    if (y < 0 || y >= ROWS || x < 0 || x >= COLS) continue;
    const row = s.board[y];
    if (row == null) continue;
    row[x] = color;
  }
  s.ctx.audio.pop();
  const cleared = clearFullRows(s);
  if (cleared > 0) {
    s.linesCleared += cleared;
    s.score += cleared * cleared * 10;
    if (s.linesCleared >= TARGET_LINES) {
      s.endedEarly = true;
      s.ctx.audio.win();
    }
  }
  if (!s.gameOver) spawnPiece(s);
}

function tryMove(s: State, dx: number): void {
  const piece = s.current;
  if (piece == null) return;
  if (!collides(s, piece.kind, piece.rotation, piece.x + dx, piece.y)) {
    piece.x += dx;
  }
}

function tryRotate(s: State): void {
  const piece = s.current;
  if (piece == null) return;
  const next = (piece.rotation + 1) % 4;
  const offsets = [0, -1, 1, -2, 2];
  for (const ox of offsets) {
    if (!collides(s, piece.kind, next, piece.x + ox, piece.y)) {
      piece.rotation = next;
      piece.x += ox;
      s.ctx.audio.beep();
      return;
    }
  }
}

function hardDrop(s: State): void {
  const piece = s.current;
  if (piece == null) return;
  while (!collides(s, piece.kind, piece.rotation, piece.x, piece.y + 1)) {
    piece.y += 1;
    s.score += 1;
  }
  s.ctx.audio.thud();
  lockPiece(s);
}

function softFall(s: State): void {
  const piece = s.current;
  if (piece == null) return;
  if (!collides(s, piece.kind, piece.rotation, piece.x, piece.y + 1)) {
    piece.y += 1;
  } else {
    lockPiece(s);
  }
}

function ghostY(s: State): number {
  const piece = s.current;
  if (piece == null) return 0;
  let y = piece.y;
  while (!collides(s, piece.kind, piece.rotation, piece.x, y + 1)) y++;
  return y;
}

function drawCell(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string,
  alpha: number,
): void {
  g.globalAlpha = alpha;
  g.fillStyle = color;
  g.fillRect(x + 1, y + 1, size - 2, size - 2);
  g.strokeStyle = 'rgba(255,255,255,0.35)';
  g.lineWidth = 1;
  g.strokeRect(x + 1.5, y + 1.5, size - 3, size - 3);
  g.globalAlpha = 1;
}

function drawStickHint(
  g: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  scale: number,
  pose: 'left' | 'right' | 'both' | 'squat',
): void {
  g.strokeStyle = '#cfd5ee';
  g.lineWidth = Math.max(2, scale * 0.05);
  g.lineCap = 'round';

  // head
  g.beginPath();
  g.arc(cx, cy - scale * 0.55, scale * 0.18, 0, Math.PI * 2);
  g.stroke();

  // body (shorter when squatting)
  const bodyBottom = pose === 'squat' ? cy + scale * 0.05 : cy + scale * 0.25;
  g.beginPath();
  g.moveTo(cx, cy - scale * 0.37);
  g.lineTo(cx, bodyBottom);
  g.stroke();

  // legs
  g.beginPath();
  if (pose === 'squat') {
    g.moveTo(cx, bodyBottom);
    g.lineTo(cx - scale * 0.3, cy + scale * 0.45);
    g.moveTo(cx, bodyBottom);
    g.lineTo(cx + scale * 0.3, cy + scale * 0.45);
  } else {
    g.moveTo(cx, bodyBottom);
    g.lineTo(cx - scale * 0.22, cy + scale * 0.6);
    g.moveTo(cx, bodyBottom);
    g.lineTo(cx + scale * 0.22, cy + scale * 0.6);
  }
  g.stroke();

  // arms
  const shoulderY = cy - scale * 0.3;
  const armUpY = cy - scale * 0.85;
  const armSideY = cy;
  g.beginPath();
  if (pose === 'left' || pose === 'both') {
    g.moveTo(cx, shoulderY);
    g.lineTo(cx - scale * 0.45, armUpY);
  } else {
    g.moveTo(cx, shoulderY);
    g.lineTo(cx - scale * 0.45, armSideY);
  }
  if (pose === 'right' || pose === 'both') {
    g.moveTo(cx, shoulderY);
    g.lineTo(cx + scale * 0.45, armUpY);
  } else {
    g.moveTo(cx, shoulderY);
    g.lineTo(cx + scale * 0.45, armSideY);
  }
  g.stroke();
}

export const poseTetris: Microgame = {
  name: NAME,
  durationSec: DURATION_SEC,
  requires: { pose: true },

  init(gctx: GameContext) {
    const s: State = {
      ctx: gctx,
      board: emptyBoard(),
      current: null,
      bag: [],
      elapsed: 0,
      calibration: CALIBRATION_SEC,
      linesCleared: 0,
      score: 0,
      fallTimer: 0,
      fallInterval: FALL_INTERVAL_MAX,
      lastMoveLeftAt: -1,
      lastMoveRightAt: -1,
      rotateLatch: false,
      dropLatch: false,
      gameOver: false,
      endedEarly: false,
      flashTimer: 0,
      flashRows: [],
    };
    refillBag(s);
    spawnPiece(s);
    state = s;
  },

  tick(dt: number, frame: InputFrame) {
    const s = state;
    if (s == null) return;
    s.elapsed += dt;

    if (s.flashTimer > 0) s.flashTimer = Math.max(0, s.flashTimer - dt);

    if (s.calibration > 0) {
      s.calibration -= dt;
      return;
    }
    if (s.gameOver || s.endedEarly) return;
    if (s.current == null) return;

    const p = frame.pose;
    if (p.visible && p.landmarks.length >= 17) {
      const ls = p.landmarks[11];
      const rs = p.landmarks[12];
      const lw = p.landmarks[15];
      const rw = p.landmarks[16];
      if (ls != null && rs != null && lw != null && rw != null) {
        const lRaised = lw.y < ls.y - ARM_RAISE_TOL_PX;
        const rRaised = rw.y < rs.y - ARM_RAISE_TOL_PX;
        const both = lRaised && rRaised;

        if (both && !s.rotateLatch) {
          tryRotate(s);
          s.rotateLatch = true;
        }
        if (!both) s.rotateLatch = false;

        if (lRaised && !both && s.elapsed - s.lastMoveLeftAt > MOVE_COOLDOWN_SEC) {
          tryMove(s, -1);
          s.lastMoveLeftAt = s.elapsed;
        }
        if (rRaised && !both && s.elapsed - s.lastMoveRightAt > MOVE_COOLDOWN_SEC) {
          tryMove(s, +1);
          s.lastMoveRightAt = s.elapsed;
        }
      }

      if (p.squatLevel > SQUAT_TRIGGER && !s.dropLatch) {
        hardDrop(s);
        s.dropLatch = true;
      }
      if (p.squatLevel < SQUAT_RELEASE) s.dropLatch = false;
    }

    if (s.gameOver || s.endedEarly || s.current == null) return;

    s.fallInterval = Math.max(
      FALL_INTERVAL_MIN,
      FALL_INTERVAL_MAX - s.score * FALL_RAMP_PER_SCORE,
    );
    s.fallTimer += dt;
    if (s.fallTimer >= s.fallInterval) {
      s.fallTimer = 0;
      softFall(s);
    }
  },

  render(g: CanvasRenderingContext2D) {
    const s = state;
    if (s == null) return;
    const { canvas } = s.ctx;
    const w = canvas.width;
    const h = canvas.height;

    g.fillStyle = '#0a0c14';
    g.fillRect(0, 0, w, h);

    const cellSize = Math.min((w * 0.45) / COLS, (h * 0.9) / ROWS);
    const boardW = cellSize * COLS;
    const boardH = cellSize * ROWS;
    const boardX = Math.max(8, (w * 0.5 - boardW) / 2);
    const boardY = (h - boardH) / 2;

    g.fillStyle = '#11141f';
    g.fillRect(boardX - 4, boardY - 4, boardW + 8, boardH + 8);
    g.strokeStyle = '#2a2f44';
    g.lineWidth = 2;
    g.strokeRect(boardX - 4, boardY - 4, boardW + 8, boardH + 8);

    g.strokeStyle = 'rgba(255,255,255,0.04)';
    g.lineWidth = 1;
    for (let x = 1; x < COLS; x++) {
      const gx = boardX + x * cellSize;
      g.beginPath(); g.moveTo(gx, boardY); g.lineTo(gx, boardY + boardH); g.stroke();
    }
    for (let y = 1; y < ROWS; y++) {
      const gy = boardY + y * cellSize;
      g.beginPath(); g.moveTo(boardX, gy); g.lineTo(boardX + boardW, gy); g.stroke();
    }

    for (let y = 0; y < ROWS; y++) {
      const row = s.board[y];
      if (row == null) continue;
      const isFlashing = s.flashTimer > 0 && s.flashRows.includes(y);
      for (let x = 0; x < COLS; x++) {
        const cell = row[x];
        if (cell == null || cell === 0) continue;
        if (isFlashing) {
          g.fillStyle = '#ffffff';
          g.fillRect(boardX + x * cellSize, boardY + y * cellSize, cellSize, cellSize);
        } else {
          drawCell(
            g,
            boardX + x * cellSize,
            boardY + y * cellSize,
            cellSize,
            colorFromIndex(cell),
            1,
          );
        }
      }
    }

    if (s.current != null && !s.gameOver) {
      const piece = s.current;
      const cells = shapeAt(piece.kind, piece.rotation);
      const gy = ghostY(s);
      for (const c of cells) {
        const xx = piece.x + c.x;
        const yy = gy + c.y;
        if (yy < 0 || yy >= ROWS || xx < 0 || xx >= COLS) continue;
        drawCell(
          g,
          boardX + xx * cellSize,
          boardY + yy * cellSize,
          cellSize,
          COLOR_BY_KIND[piece.kind],
          0.18,
        );
      }
      for (const c of cells) {
        const xx = piece.x + c.x;
        const yy = piece.y + c.y;
        if (yy < 0 || yy >= ROWS || xx < 0 || xx >= COLS) continue;
        drawCell(
          g,
          boardX + xx * cellSize,
          boardY + yy * cellSize,
          cellSize,
          COLOR_BY_KIND[piece.kind],
          1,
        );
      }
    }

    const rightX = w * 0.55;
    const rightW = w * 0.42;
    g.fillStyle = '#fff';
    g.textAlign = 'left';
    g.textBaseline = 'top';
    g.font = `bold ${Math.round(h * 0.045)}px system-ui, sans-serif`;
    g.fillText(`LINES ${s.linesCleared} / ${TARGET_LINES}`, rightX, h * 0.06);
    g.font = `${Math.round(h * 0.032)}px system-ui, sans-serif`;
    g.fillStyle = '#cfd5ee';
    g.fillText(`SCORE ${s.score}`, rightX, h * 0.13);
    const remain = Math.max(0, DURATION_SEC - s.elapsed);
    g.fillText(`TIME ${remain.toFixed(1)}s`, rightX, h * 0.18);

    const hintScale = Math.min(rightW * 0.18, h * 0.09);
    const hintY = h * 0.42;
    const hintGap = rightW / 4;
    const hints: Array<['left' | 'right' | 'both' | 'squat', string]> = [
      ['left', 'LEFT'],
      ['right', 'RIGHT'],
      ['both', 'ROTATE'],
      ['squat', 'DROP'],
    ];
    g.textAlign = 'center';
    g.textBaseline = 'top';
    g.font = `${Math.round(h * 0.022)}px system-ui, sans-serif`;
    for (let i = 0; i < hints.length; i++) {
      const entry = hints[i];
      if (entry == null) continue;
      const [pose, label] = entry;
      const cx = rightX + hintGap * (i + 0.5);
      drawStickHint(g, cx, hintY, hintScale, pose);
      g.fillStyle = '#9aa0bf';
      g.fillText(label, cx, hintY + hintScale * 0.85);
    }

    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = `${Math.round(h * 0.022)}px system-ui, sans-serif`;
    g.fillStyle = '#7d8298';
    g.fillText('raise arms to nudge • both arms to rotate • squat to slam', rightX + rightW / 2, h * 0.68);

    if (s.calibration > 0) {
      g.fillStyle = 'rgba(8, 10, 18, 0.7)';
      g.fillRect(0, 0, w, h);
      g.fillStyle = '#ffd86b';
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.font = `bold ${Math.round(h * 0.06)}px system-ui, sans-serif`;
      g.fillText('STAND IN T-POSE', w / 2, h * 0.45);
      g.fillStyle = '#fff';
      g.font = `bold ${Math.round(h * 0.08)}px system-ui, sans-serif`;
      g.fillText(`${s.calibration.toFixed(1)}s`, w / 2, h * 0.55);
    }

    if (s.gameOver) {
      g.fillStyle = 'rgba(20, 5, 10, 0.72)';
      g.fillRect(0, 0, w, h);
      g.fillStyle = '#ff5d7a';
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.font = `bold ${Math.round(h * 0.085)}px system-ui, sans-serif`;
      g.fillText('STACK OVERFLOW', w / 2, h * 0.5);
      g.fillStyle = '#fff';
      g.font = `${Math.round(h * 0.035)}px system-ui, sans-serif`;
      g.fillText(`lines cleared: ${s.linesCleared}`, w / 2, h * 0.6);
    } else if (s.endedEarly) {
      g.fillStyle = 'rgba(8, 24, 16, 0.55)';
      g.fillRect(0, 0, w, h);
      g.fillStyle = '#7dffb1';
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.font = `bold ${Math.round(h * 0.08)}px system-ui, sans-serif`;
      g.fillText('LINES CLEARED', w / 2, h * 0.5);
    }
  },

  end(): MicrogameResult {
    const s = state;
    if (s == null) {
      return {
        microgame: NAME,
        won: false,
        score: 0,
        detail: { lines: 0, gameOver: 1 },
      };
    }
    const won = s.linesCleared >= TARGET_LINES;
    if (won && !s.endedEarly && !s.gameOver) s.ctx.audio.win();
    if (!won && !s.gameOver) s.ctx.audio.lose();
    return {
      microgame: NAME,
      won,
      score: s.linesCleared * 100 + s.score,
      detail: {
        lines: s.linesCleared,
        gameOver: s.gameOver ? 1 : 0,
      },
    };
  },
};
